import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings, Check, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'ais-cookie-consent-v1';

// PostHog configuration (matches index.html snippet)
const POSTHOG_KEY = 'phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE';
const POSTHOG_HOST = 'https://us.i.posthog.com';

const defaultConsent = { essential: true, analytics: false };

// Read/write consent from localStorage
export const getConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const saveConsent = (consent) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...consent, savedAt: new Date().toISOString() })
    );
  } catch {
    /* noop */
  }
};

// Apply consent — enable or disable analytics
const applyConsent = (consent) => {
  if (typeof window === 'undefined' || !window.posthog) return;

  if (consent.analytics) {
    // Initialize PostHog if not already done
    if (!window.__posthog_initialized) {
      try {
        window.posthog.init(POSTHOG_KEY, {
          api_host: POSTHOG_HOST,
          person_profiles: 'identified_only',
        });
        window.__posthog_initialized = true;
      } catch (e) {
        console.warn('PostHog init failed:', e);
      }
    } else if (window.posthog.opt_in_capturing) {
      window.posthog.opt_in_capturing();
    }
  } else if (window.__posthog_initialized && window.posthog.opt_out_capturing) {
    window.posthog.opt_out_capturing();
  }
};

// Public helper for footer "Cookies" link — opens preferences
let openBannerExternal = null;
export const openCookiePreferences = () => {
  if (openBannerExternal) openBannerExternal();
};

export const CookieBanner = () => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  useEffect(() => {
    const existing = getConsent();
    if (existing) {
      applyConsent(existing);
      setAnalyticsEnabled(!!existing.analytics);
    } else {
      // First visit — show banner
      setVisible(true);
    }

    // Expose external opener for footer link
    openBannerExternal = () => {
      const current = getConsent() || defaultConsent;
      setAnalyticsEnabled(!!current.analytics);
      setShowPrefs(true);
      setVisible(true);
    };

    return () => {
      openBannerExternal = null;
    };
  }, []);

  const handleAcceptAll = () => {
    const consent = { essential: true, analytics: true };
    saveConsent(consent);
    applyConsent(consent);
    setVisible(false);
    setShowPrefs(false);
  };

  const handleRejectAll = () => {
    const consent = { essential: true, analytics: false };
    saveConsent(consent);
    applyConsent(consent);
    setVisible(false);
    setShowPrefs(false);
  };

  const handleSavePrefs = () => {
    const consent = { essential: true, analytics: analyticsEnabled };
    saveConsent(consent);
    applyConsent(consent);
    setVisible(false);
    setShowPrefs(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] animate-in slide-in-from-bottom duration-300"
      data-testid="cookie-banner"
    >
      <div className="bg-white border-t-2 border-emerald-500 shadow-2xl">
        <div className="container mx-auto px-4 py-4 sm:px-6">
          {!showPrefs ? (
            // --- Main banner ---
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Cookie className="h-5 w-5 text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 text-sm sm:text-base mb-1">
                    We use cookies
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    We use essential cookies to run this site and optional analytics cookies
                    (PostHog) to understand how visitors use it — so we can improve the service.
                    You can accept all, reject all non-essential, or manage preferences. Read our{' '}
                    <button
                      onClick={() => {
                        setVisible(false);
                        navigate('/cookies');
                      }}
                      className="text-emerald-700 underline hover:text-emerald-800 font-medium"
                    >
                      Cookie Policy
                    </button>
                    .
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 lg:flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPrefs(true)}
                  className="text-xs sm:text-sm"
                  data-testid="cookie-manage-btn"
                >
                  <Settings className="h-3.5 w-3.5 mr-1.5" /> Manage
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRejectAll}
                  className="text-xs sm:text-sm border-slate-400 text-slate-700 hover:bg-slate-100"
                  data-testid="cookie-reject-btn"
                >
                  Reject All
                </Button>
                <Button
                  size="sm"
                  onClick={handleAcceptAll}
                  className="text-xs sm:text-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="cookie-accept-btn"
                >
                  <Check className="h-3.5 w-3.5 mr-1.5" /> Accept All
                </Button>
              </div>
            </div>
          ) : (
            // --- Preferences view ---
            <div className="space-y-3 max-w-3xl mx-auto">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-emerald-700" />
                  <h3 className="font-semibold text-slate-900 text-base">
                    Cookie Preferences
                  </h3>
                </div>
                <button
                  onClick={() => setShowPrefs(false)}
                  className="text-slate-500 hover:text-slate-700"
                  aria-label="Close preferences"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Essential */}
              <div className="p-3 border border-slate-200 rounded-lg bg-slate-50">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 text-sm">
                        Essential Cookies
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-medium">
                        Always On
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">
                      Required for the site to function (booking form, tracking, admin login).
                      Cannot be disabled.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked
                    disabled
                    className="h-4 w-4 mt-1 accent-emerald-600"
                    aria-label="Essential cookies (always on)"
                  />
                </div>
              </div>

              {/* Analytics */}
              <div className="p-3 border border-slate-200 rounded-lg">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <span className="font-medium text-slate-900 text-sm">
                      Analytics Cookies (PostHog)
                    </span>
                    <p className="text-xs text-slate-600 mt-1">
                      Help us understand which pages are popular and where visitors click, so we
                      can improve your experience. Data is anonymous.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={analyticsEnabled}
                    onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                    className="h-4 w-4 mt-1 accent-emerald-600"
                    data-testid="cookie-analytics-toggle"
                    aria-label="Toggle analytics cookies"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRejectAll}
                  className="flex-1 sm:flex-initial"
                >
                  Reject All
                </Button>
                <Button
                  size="sm"
                  onClick={handleSavePrefs}
                  className="flex-1 sm:flex-initial bg-emerald-600 hover:bg-emerald-700"
                  data-testid="cookie-save-prefs-btn"
                >
                  Save Preferences
                </Button>
                <Button
                  size="sm"
                  onClick={handleAcceptAll}
                  className="flex-1 sm:flex-initial bg-slate-800 hover:bg-slate-900"
                >
                  Accept All
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
